import { Overlay, OverlayContainer, ViewportRuler } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { Location, NgClass, NgStyle } from '@angular/common';
import { Component, Injector, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { SafeHtmlPipe } from '@app/finxone-web-frontend/app/lib/pipes/SafeHtml.pipe';
import { stripModalFromLocation } from '@app/finxone-web-frontend/app/Modules/zones/modal-utils';
import { PageConfigurationProvider } from '@app/finxone-web-frontend/app/Modules/zones/page-configuration.provider';
import { CloseModalPopupAction } from '@app/finxone-web-frontend/app/lib/actions/page-context.action';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { WorkflowFormGuard } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-form.guard';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { scrollAndUpdateFragment } from '@app/finxone-web-frontend/app/lib/utils/form-widget-action-utils/input-widget-verify-button-action.utils';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  BaseCssProperties,
  ProviderClass,
  UiZoneWidgetConfig,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Subject, takeUntil } from 'rxjs';
import { OverlayWidgetComponent } from '../../overlay/overlay';

@Component({
  selector: 'finxone-web-frontend-back-button',
  templateUrl: './back-widget.component.html',
  styleUrls: ['./back-widget.component.scss'],
  imports: [NgClass, NgStyle, MatIconModule, SafeHtmlPipe],
})
export class BackButtonComponent extends OverlayWidgetComponent<BackButtonComponent> implements OnInit {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  public widgetConfig: UiZoneWidgetConfig;
  public activePageIndex!: number;
  public backgroundShape: string;
  public icon: string;
  public iconSize?: baseCssConfigStyle;
  public text: string;
  public renderedContent: string;
  public globalStylingClass = '';
  public isAllowedGlobalStyling = false;

  constructor(
    configService: ConfigService,
    dataIn: ProviderClass<any>,
    private location: Location,
    overlay: Overlay,
    injector: Injector,
    _viewportRuler: ViewportRuler,
    _platform: Platform,
    _overlayContainer: OverlayContainer,
    public pageConfig: PageConfigurationProvider,
    private store: Store,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    private route: ActivatedRoute,
    private workflowFormsService: WorkflowFormsService,
    private router: Router,
  ) {
    super(
      dataIn,
      overlay,
      injector,
      BackButtonComponent,
      _viewportRuler,
      _platform,
      _overlayContainer,
      configService,
    );
    if (this.dataIn != null) {
      this.attri = this.dataIn.attri;
      this.widgetConfig = this.dataIn.widgetConfig;
      this.backgroundShape = this.widgetConfig.attributes.widgetProperties['backgroundShape'];
      this.icon = this.dataIn.attri?.widgetProperties?.icon ?? 'arrow-left';
      this.text = this.dataIn.attri?.widgetProperties?.textContent ?? '';
    }
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.COLOR,
      BaseCssProperties.TEXTALIGN,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.BORDER,
      BaseCssProperties.V_ALIGN,
      BaseCssProperties.TEXTALIGN,
      BaseCssProperties.FONT_SIZE,
      BaseCssProperties.TEXT_ITALIC,
      BaseCssProperties.TEXT_BOLD,
      BaseCssProperties.TEXT_FONT_STYLE,
      BaseCssProperties.BOX_SHADOW,
      BaseCssProperties.TEXT_UNDERLINE,
    ]);

    this.iconSize = this.configService.getSpecificBaseCssStyle(this.attri, [BaseCssProperties.ICON_SIZE]);
    this.handleBackgroundShape(this.backgroundShape);
    if (!this.overlayActive) {
      this.contentStyle['bottom'] = '0';
      this.contentStyle['left'] = '0';
      this.contentStyle['top'] = '0';
      this.contentStyle['right'] = '0';
    } else {
      this.contentStyle['width'] = '100%';
      this.contentStyle['height'] = '100%';
    }
  }

  ngOnInit() {
    const template = Handlebars.compile(this.text);
    buildHandleBarModel(this.store, this.text, this.countriesCurrenciesService, this.iconCacheService)
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.renderedContent = filterIfHandlebarNotRendered(template(model));
      });
    this.getGlobalStyling();
  }

  /**
   * Handles navigation when the user requests to go to the previous screen (e.g., clicking a 'Back' button).
   *
   * 1. If a modal is open ('modal:' is in the path):
   * - It checks the 'isModal' query parameter (or its absence) to determine the *previous* navigation context.
   * - If the previous page was also a modal (or the context is undefined), it uses location.back(),
   * relying on the browser history to go back one step (which may navigate two steps back if history wasn't managed).
   * - Otherwise (if returning to a full page), it uses the router to explicitly navigate to the stripped
   * parent URL, ensuring the history stack is corrected via 'replaceUrl: true'.
   * - Finally, it dispatches the CloseModalPopupAction.
   * 2. If no modal is open, it defaults to using location.back().
   */
  public handlePrevAction() {
    const isCurrentPathModal = this.location.path().includes('modal:');

    // Disable form validation check for navigating to the previous page
    WorkflowFormGuard.disableFormValidation();
    this.workflowFormsService.markAllCurrentPageFormsAsValid();
    if (isCurrentPathModal) {
      // modal navigation
      const isModalQueryParam = this.route.snapshot?.queryParams?.['isModal'];
      const isPrevModal = isModalQueryParam === 'true';

      if (isPrevModal || isModalQueryParam == null) {
        this.location.back();
      } else {
        this.router.navigate([stripModalFromLocation()], { replaceUrl: true });
      }

      this.store.dispatch(new CloseModalPopupAction(isPrevModal));
    } else {
      // default navigation
      this.location.back();
    }
    scrollAndUpdateFragment(this.router);
  }

  private getGlobalStyling() {
    if (
      this.dataIn.attri.widgetProperties?.['globalStylingClass'] &&
      this.dataIn.attri.widgetProperties?.['globalStyling']
    ) {
      this.globalStylingClass = this.dataIn.attri.widgetProperties?.['globalStylingClass'] ?? '';
      this.isAllowedGlobalStyling = this.dataIn.attri.widgetProperties?.['globalStyling'] ?? false;
    }
  }

  public handleBackgroundShape(shape: string) {
    switch (shape) {
      case 'circle':
        this.contentStyle['border-bottom-left-radius'] = '50%';
        this.contentStyle['border-top-left-radius'] = '50%';
        this.contentStyle['border-bottom-right-radius'] = '50%';
        this.contentStyle['border-top-right-radius'] = '50%';
        break;
      case 'square':
      case 'rectangle':
        this.contentStyle['border-bottom-left-radius'] = '0%';
        this.contentStyle['border-top-left-radius'] = '0%';
        this.contentStyle['border-bottom-right-radius'] = '0%';
        break;
    }
  }
}
